import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { MonetizationOn, CheckCircle, Warning } from '@mui/icons-material';
import { useApiClient } from '../services/api';

interface PayoutModalProps {
  open: boolean;
  onClose: () => void;
  team: {
    id: string;
    team_name: string;
    project_title?: string;
    participants: Array<{
      id: string;
      name: string;
      email: string;
    }>;
  };
  bountyId: string;
  bountyTitle: string;
  suggestedAmount: number;
  currency: string;
  onSuccess?: () => void;
}

const PayoutModal: React.FC<PayoutModalProps> = ({
  open,
  onClose,
  team,
  bountyId,
  bountyTitle,
  suggestedAmount,
  currency,
  onSuccess,
}) => {
  const apiClient = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Check for connected accounts
  const [checkingAccounts, setCheckingAccounts] = useState(true);
  const [accountsStatus, setAccountsStatus] = useState<{
    missingAccounts: string[];
    allReady: boolean;
  }>({ missingAccounts: [], allReady: false });

  // Form state
  const [amount, setAmount] = useState<string>((suggestedAmount / 100).toFixed(2));
  const [selectedCurrency, setSelectedCurrency] = useState(currency.toLowerCase());
  const [description, setDescription] = useState(
    `Bounty payout for "${bountyTitle}" - ${team.project_title || team.team_name}`
  );
  const [recipientIndex, setRecipientIndex] = useState<number>(0);

  useEffect(() => {
    if (open) {
      checkConnectedAccounts();
      setSuccess(false);
      setError(null);
    }
  }, [open, team.participants]);

  const checkConnectedAccounts = async () => {
    setCheckingAccounts(true);
    const missing: string[] = [];

    // For MVP, we skip checking individual participant accounts
    // The backend will validate when the payout is attempted
    // This prevents privacy issues with checking other users' account status

    setAccountsStatus({
      missingAccounts: missing,
      allReady: true, // Assume ready, backend will validate
    });
    setCheckingAccounts(false);
  };

  const handleSendPayout = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate amount
      const amountInCents = Math.round(parseFloat(amount) * 100);
      if (isNaN(amountInCents) || amountInCents <= 0) {
        setError('Please enter a valid amount');
        setLoading(false);
        return;
      }

      // Get recipient
      const recipient = team.participants[recipientIndex];
      if (!recipient) {
        setError('No valid recipient selected');
        setLoading(false);
        return;
      }

      // Create bounty payout using the new endpoint
      const response = await apiClient.createBountyPayout({
        recipientUserId: recipient.id,
        amount: amountInCents,
        currency: selectedCurrency,
        bountyId,
        description,
        metadata: {
          teamId: team.id,
          teamName: team.team_name,
          projectTitle: team.project_title,
          recipientName: recipient.name,
          recipientEmail: recipient.email,
        },
      });

      if (!response.success) {
        setError(response.message || 'Failed to send payout');
        setLoading(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 2000);
    } catch (err) {
      setError('Failed to send payout');
      console.error('Error sending payout:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <MonetizationOn color="primary" />
          <Typography variant="h6">Send Payout</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {checkingAccounts && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={40} />
          </Box>
        )}

        {!checkingAccounts && (
          <>
            {/* Team Info */}
            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Team
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {team.team_name}
              </Typography>
              {team.project_title && (
                <Typography variant="body2" color="text.secondary">
                  Project: {team.project_title}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Bounty Info */}
            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Bounty
              </Typography>
              <Typography variant="body1">{bountyTitle}</Typography>
              <Typography variant="caption" color="text.secondary">
                Suggested amount: {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: currency,
                }).format(suggestedAmount / 100)}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Warning about connected accounts */}
            {!accountsStatus.allReady && accountsStatus.missingAccounts.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Some team members don't have connected accounts
                </Typography>
                <Typography variant="caption">
                  Missing accounts: {accountsStatus.missingAccounts.join(', ')}
                </Typography>
              </Alert>
            )}

            {/* Recipient Selection */}
            <TextField
              select
              fullWidth
              label="Send Payment To"
              value={recipientIndex}
              onChange={(e) => setRecipientIndex(Number(e.target.value))}
              sx={{ mb: 2 }}
              helperText="Select the team member who will receive the payout"
            >
              {team.participants.map((participant, index) => (
                <MenuItem key={participant.id} value={index}>
                  <Box>
                    <Typography variant="body2">{participant.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {participant.email}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {/* Amount Input */}
            <Box display="flex" gap={2} mb={2}>
              <TextField
                label="Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                required
                inputProps={{ step: '0.01', min: '0' }}
                helperText="Enter amount in dollars (e.g., 100.00)"
              />
              <TextField
                select
                label="Currency"
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                sx={{ minWidth: '120px' }}
              >
                <MenuItem value="usd">USD</MenuItem>
                <MenuItem value="eur">EUR</MenuItem>
                <MenuItem value="gbp">GBP</MenuItem>
              </TextField>
            </Box>

            {/* Description */}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
              helperText="Optional: Add a description for this payout"
            />

            {/* Payout Summary */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Payout Summary
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding>
                  <ListItemText
                    primary="Recipient"
                    secondary={team.participants[recipientIndex]?.name}
                    primaryTypographyProps={{ variant: 'caption' }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary="Amount"
                    secondary={new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: selectedCurrency.toUpperCase(),
                    }).format(parseFloat(amount) || 0)}
                    primaryTypographyProps={{ variant: 'caption' }}
                    secondaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                  />
                </ListItem>
              </List>
            </Alert>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Success Alert */}
            {success && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  Payout sent successfully!
                </Typography>
                <Typography variant="caption">
                  The funds will be transferred to the recipient's connected account.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading || success}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handleSendPayout}
          variant="contained"
          disabled={loading || success || checkingAccounts || !amount}
          startIcon={loading ? <CircularProgress size={20} /> : <MonetizationOn />}
        >
          {loading ? 'Sending...' : success ? 'Sent!' : 'Send Payout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayoutModal;
